import React, { PureComponent } from 'react'
import { FormattedMessage, FormattedDate, FormattedTime, injectIntl } from 'react-intl'
import * as Sentry from '@sentry/browser'
import { utcToZonedTime } from 'date-fns-tz'
import { Link } from 'react-router-dom'
import Spinner from '../components/Spinner'
import Button from '../components/Button'
import AuthenticatedAPI from '../lib/authenticatedAPI'
import ErrorBlock from '../components/ErrorBlock'
import MembershipBlock from '../components/dashboardPage/MembershipBlock'
import HelpButton from '../components/HelpButton'
// import { fixLegacyData, fetchParticipants } from '../lib/booking'
import { fetchParticipants } from '../lib/booking'

class DashboardPage extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      bookings: [],
      userInfo: {},
      membershipsList: [],
      isLoading: true
    }
    this.keyCount = 0
    this.getKey = this.getKey.bind(this)
  }

  componentWillMount() {
    window.scrollTo(0, 0); // TODO avoid duplication on all pages
  }

  async componentDidMount() {
    try {
      const [userInfo, bookings, allBookingOrdersCombined] = await this.userInfoAndUpcomingBookingsAndAllOrders()
      userInfo.participants = await fetchParticipants()
      const membershipsList = this.buildMembershipList(userInfo)

      this.setState({ userInfo, bookings, allBookingOrdersCombined, membershipsList, isLoading: false, errorMsg: null })
    } catch (error) {
      Sentry.captureException(error)
      this.setState({ errorMsg: error.message, isLoading: false })
      // eslint-disable-next-line no-console
      console.error(error)
    }
  }

  getKey() {
    this.keyCount += 1
    return this.keyCount
  }

  userInfoAndUpcomingBookingsAndAllOrders = () => Promise.all([
    AuthenticatedAPI.get('cphill', '/users'),
    AuthenticatedAPI.get('cphill', '/users/bookings'),
    AuthenticatedAPI.get('cphill', '/users/bookingOrders')
  ])

  buildMembershipList = userInfo => {

    const ownMembership = userInfo.memberships
      .filter(m => m.isBookersOwn && !m.status).pop()

    const membershipAsParticipant = membership => ({
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      membership,
      membershipId: membership.id
    })

    return [
      ...(ownMembership ? [membershipAsParticipant(ownMembership)] : []),
      ...(userInfo.participants && userInfo.participants.filter(p => p.membershipId) || [])
    ]
  }

  errorBlock = (errorMsg) => (
    <div className="container mx-auto">
      <ErrorBlock headerText="An error occured" descriptionText={errorMsg} />
      Click here to&nbsp;
      <button onClick={() => { window.location.reload() }} className="font-bold mt-4" type="button">
        reload
      </button>
      &nbsp;page
    </div>
  )

  gotoNewBooking = () => {
    const { props } = this
    props.history.push(`/bookings`)
  }

  gotoBooking = (booking) => {
    const { props } = this
    props.history.push(`/bookings/${booking.id}`)
  }

  listOfHistoricMembershipIds = membership => {
    const { state } = this
    const list = []
    const recursiveFinder = id => {
      if (!membership.bookingOrderId // Expired automatically downgraded membership don't have a bookingOrder
        || state.allBookingOrdersCombined.find(o => o.membershipId === id)
      ) {
        list.push(id)
        const replaced = state.userInfo.memberships.find(m => m.replacedByMembershipId === id)
        if (replaced) {
          recursiveFinder(replaced.id)
        }
      }
    }
    recursiveFinder(membership.id)
    return list
  }

  render() {
    const { props, state } = this
    
    if (state.isLoading) {
      return <Spinner />
    }
    
    if (state.errorMsg) {
      return this.errorBlock(state.errorMsg)
    }
    
    const { intl } = props
    const timelimit = new Date()
    timelimit.setDate(timelimit.getDate() + 2)

    return (
      <div className="container mx-auto px-2 md:px-0">
        <div className="lg:w-2/3 xl:w-3/5 md:mt-10 mb-12 mx-auto">
          <h4 className="text-harbor-80 uppercase text-sky">
            <FormattedMessage id="dashboard.category" defaultMessage="Profile" />
          </h4>

          <div className="border-b pb-6">
            <h1 className="mb-0 text-2xl md:text-3xl">
              {`${state.userInfo.firstName || ''} ${state.userInfo.lastName || ''}`}
            </h1>
            <h3 className="text-grey">
              {state.userInfo.email}
            </h3>
          </div>

          <h4 className="text-harbor-80 uppercase text-sky mt-6 mb-0">
            <FormattedMessage id="dashboard.memberships.header" defaultMessage="Memberships" />
          </h4>

          <div className="flex flex-wrap md:mt-4">
            {state.userInfo && state.membershipsList.map((participant, index) => {
              const membership = participant.membership || { type: 'passHourlyAdult' }
              membership.bookingOrders = state.allBookingOrdersCombined
                .filter(order => this.listOfHistoricMembershipIds(membership).includes(order.membershipId))

              return (
                <div key={this.getKey()} className="w-full md:w-1/2 mt-4">
                  <MembershipBlock
                    participant={participant}
                    bookingOrders={membership.bookingOrders.filter(b => b.booking)}
                    membership={membership}
                    index={index}
                    skus={props.skus}
                  />
                </div>
              )
            })}
          </div>

          <h4 className="text-harbor-80 uppercase text-sky mt-10 mb-0">
            <FormattedMessage
              id="dashboard.bookings.header"
              defaultMessage="Bookings"
            />
          </h4>

          <div className="mt-4">
            {state.bookings.map((booking) => {
              const [dateStart, dateEnd] = [booking.dateStart, booking.dateEnd].map(d => utcToZonedTime(d, 'Europe/Copenhagen'))
              return (
                <div
                  key={this.getKey()}
                  className="flex border-b py-2 items-end sm:items-center"
                >
                  <div className="flex-grow">
                    <div className="font-bold">
                      <FormattedDate
                        value={dateStart}
                        year='numeric'
                        month='long'
                        day='numeric'
                      />
                    </div>
                    <div className="text-xs font-bold text-sky">
                      <FormattedTime value={dateStart} />
                    &nbsp;-&nbsp;
                      <FormattedTime value={dateEnd} />
                    </div>
                    <div className="text-xs mt-2">
                      {`${booking.bookingOrders.length} ${booking.bookingOrders.length === 1 ? 'person' : intl.formatMessage({ id: 'people', defaultMessage: 'people'})}`}
                    </div>
                  </div>
                  {dateStart > timelimit ? (
                    <Link
                      to={`/bookings/${booking.id}/edit`}
                      className="text-grass text-sm uppercase underline font-bold"
                    >
                      <FormattedMessage
                        id="dashboard.bookings.edit"
                        defaultMessage="Edit date/time"
                      />
                      {/* Rediger dato/tid */}
                    </Link>
                  ) : (
                    <div className="flex items-center">
                      <div className="text-grey text-sm uppercase font-bold italic mr-2">
                        <FormattedMessage
                          id="dashboard.bookings.notEditable"
                          defaultMessage="Not editable"
                        />
                        {/* Ikke redigerbar */}
                      </div>
                      <HelpButton onClick={() => {}} />
                    </div>
                  )}
                </div>
              )
            })}
            <div className="flex mt-8">
              <div className="flex-grow">
                <Button
                  onClick={() => { props.history.goBack() }}
                  bgCss='bg-grey'
                >
                  <FormattedMessage
                    id="back"
                    defaultMessage="Back"
                  />
                </Button>
              </div>

              <Button
                onClick={() => this.gotoNewBooking()}
                className="bg-grass"
              >
                <FormattedMessage
                  id="dashboard.newBooking"
                  defaultMessage="New booking"
                />
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

DashboardPage.propTypes = {}

export default injectIntl(DashboardPage)